import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';

const Chatbot = (props) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'bot',
      content: "Hello! 👋 Welcome to Rabuste Coffee! I'm your virtual assistant here to help you discover everything we offer:\n\n☕ **Coffee Menu** - Exclusive Robusta coffee, Shakes, Sides, and Tea\n🎨 **Art Gallery** - Browse and purchase fine art pieces\n🎭 **Artist Submission** - Submit your artwork for consideration\n📚 **Workshops** - Coffee workshops, art sessions, and community events\n🚀 **Franchise** - Explore franchise opportunities\n📱 **Ordering** - QR code ordering or counter service\n📦 **Pre-Orders** - Schedule your pickup time\n📊 **Order Tracking** - Track your orders\n🎁 **Daily Offers** - Check out current specials\n\nI can help you with menu recommendations, answer questions about our coffee, guide you to different pages, or assist with ordering. What would you like to know?",
    },
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (props.onOpenChange) {
      props.onOpenChange(isOpen);
    }
  }, [isOpen, props]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = { role: 'user', content: input };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const conversationHistory = messages.map((msg) => ({
        role: msg.role,
        content: msg.content,
      }));

      const response = await api.post('/ai/chatbot', {
        message: input,
        conversationHistory,
      });

      const botResponse = response.data.response;

      // Check if response includes navigation instruction
      if (response.data.navigateTo) {
        setMessages((prev) => [
          ...prev,
          { role: 'bot', content: botResponse + ' 🚀' },
        ]);
        // Small delay before navigation for better UX
        setTimeout(() => {
          navigate(response.data.navigateTo);
          setIsOpen(false);
        }, 1500);
      } else {
        setMessages((prev) => [
          ...prev,
          { role: 'bot', content: botResponse },
        ]);
      }
    } catch (error) {
      console.error('Chatbot error:', error);
      setMessages((prev) => [
        ...prev,
        {
          role: 'bot',
          content: 'Sorry, I encountered an error. Please try again or visit our pages for more information.',
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Chatbot Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-8 right-8 z-40 bg-coffee-amber text-coffee-darker rounded-full p-4 shadow-2xl hover:bg-coffee-gold transition-colors"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        aria-label="Open chatbot"
      >
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>
      </motion.button>

      {/* Chatbot Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            className="fixed bottom-24 right-8 z-40 w-96 max-w-[calc(100vw-2rem)] h-[500px] bg-coffee-darker border-2 border-coffee-brown rounded-lg shadow-2xl flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="bg-coffee-brown px-4 py-3 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                <span className="font-semibold text-coffee-cream">
                  Rabuste Assistant
                </span>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-coffee-cream hover:text-coffee-amber"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg px-4 py-2 ${msg.role === 'user'
                      ? 'bg-coffee-amber text-coffee-darker'
                      : 'bg-coffee-brown text-coffee-cream'
                      }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-coffee-brown text-coffee-cream rounded-lg px-4 py-2">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-coffee-cream rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-coffee-cream rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-coffee-cream rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="border-t border-coffee-brown p-4">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                  placeholder="Ask about menu, art, workshops, ordering, offers, franchise..."
                  className="flex-1 bg-coffee-brown text-coffee-cream px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-coffee-amber"
                  disabled={isLoading}
                />
                <button
                  onClick={handleSend}
                  disabled={isLoading || !input.trim()}
                  className="bg-coffee-amber text-coffee-darker px-3 py-2 rounded-lg hover:bg-coffee-gold disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                  aria-label="Send message"
                >
                  <svg
                    className="w-5 h-5 transform rotate-90"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Chatbot;

